import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceMembersResponse,
  WorkspaceUsage,
} from '../../models/workspace.interface';
import { LoggerService } from './logger.service';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  private apiAddress: string = environment.apiAddress;
  private readonly STORAGE_KEY = 'current_workspace_id';
  private currentWorkspace$ = new BehaviorSubject<Workspace | null>(null);

  /**
   * GLOB-1897: `/workspace/current` is the only source of the membership that
   * drives PermissionService, and PermissionService is fail-closed. When the
   * request fails the UI silently loses menu entries, which is
   * indistinguishable from a role that legitimately lacks them. Callers and the
   * layout can subscribe here to tell the two apart.
   */
  private loadError$ = new BehaviorSubject<HttpErrorResponse | null>(null);

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private router: Router,
    private permissionService: PermissionService
  ) {
    // Load workspace from storage on initialization
    this.loadWorkspaceFromStorage();
  }

  /**
   * Get current workspace from API.
   */
  getCurrentWorkspace(): Observable<Workspace> {
    return this.http
      .get<{ code: number; message: string; data: Workspace }>(
        `${this.apiAddress}/workspace/current`
      )
      .pipe(
        map((response) => response.data),
        tap((workspace) => {
          this.setCurrentWorkspace(workspace.id);
          this.currentWorkspace$.next(workspace);
          this.permissionService.setMembership(workspace.membership ?? null);
          this.loadError$.next(null);
        }),
        catchError((error) => {
          // GLOB-1897: this used to swallow the failure whole. The cleanup that
          // lived in loadWorkspaceFromStorage's error handler never ran, because
          // returning of(null) means the observable never errors.
          this.handleCurrentWorkspaceFailure(error);
          return of(null as any);
        })
      );
  }

  /**
   * Record a `/workspace/current` failure and, when the workspace is provably
   * unusable, drop the stale selection.
   *
   * A transient failure (offline, 5xx) must not discard the user's workspace
   * choice, so only an explicit rejection clears it.
   */
  private handleCurrentWorkspaceFailure(error: HttpErrorResponse): void {
    this.logger.error('Error getting current workspace', error);
    this.loadError$.next(error);

    const workspaceIsUnusable = [400, 401, 403, 404].includes(error?.status);
    if (workspaceIsUnusable) {
      localStorage.removeItem(this.STORAGE_KEY);
      this.currentWorkspace$.next(null);
      this.permissionService.clear();
    }
  }

  /**
   * Emits the last `/workspace/current` failure, or null once a load succeeds.
   * A non-null value means the permission state is unknown rather than empty.
   */
  getLoadErrorObservable(): Observable<HttpErrorResponse | null> {
    return this.loadError$.asObservable();
  }

  /** True when the last attempt to load the current workspace failed. */
  hasLoadError(): boolean {
    return this.loadError$.value !== null;
  }

  /**
   * Get the workspace owned by the current user, independent of whichever
   * workspace is active in session. Used by "Mi cuenta" so usage/limits shown
   * there always reflect the user's own workspace, even while browsing
   * another workspace as a guest. Returns null if the user owns no workspace.
   */
  getOwnedWorkspace(): Observable<Workspace | null> {
    return this.http
      .get<{ code: number; message: string; data: Workspace }>(`${this.apiAddress}/workspace/owned`)
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          if (error?.status !== 404) {
            this.logger.error('Error getting owned workspace', error);
          }
          return of(null);
        })
      );
  }

  /**
   * Get all workspaces for the current user.
   */
  getUserWorkspaces(): Observable<Workspace[]> {
    return this.http
      .get<{ code: number; message: string; data: Workspace[] }>(`${this.apiAddress}/workspace`)
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error getting user workspaces', error);
          return of([]);
        })
      );
  }

  /**
   * Get workspace by ID.
   */
  getWorkspace(id: string): Observable<Workspace> {
    return this.http
      .get<{ code: number; message: string; data: Workspace }>(`${this.apiAddress}/workspace/${id}`)
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error getting workspace', error);
          throw error;
        })
      );
  }

  /**
   * Create a new workspace.
   */
  createWorkspace(name: string): Observable<Workspace> {
    return this.http
      .post<{ code: number; message: string; data: Workspace }>(`${this.apiAddress}/workspace`, {
        name,
      })
      .pipe(
        map((response) => response.data),
        tap((workspace) => {
          this.setCurrentWorkspace(workspace.id);
          this.currentWorkspace$.next(workspace);
        }),
        catchError((error) => {
          this.logger.error('Error creating workspace', error);
          throw error;
        })
      );
  }

  /**
   * Update workspace.
   */
  updateWorkspace(id: string, data: { name?: string; settings?: any }): Observable<Workspace> {
    return this.http
      .put<{ code: number; message: string; data: Workspace }>(
        `${this.apiAddress}/workspace/${id}`,
        data
      )
      .pipe(
        map((response) => response.data),
        tap((workspace) => {
          if (this.getCurrentWorkspaceFromStorage() === id) {
            this.currentWorkspace$.next(workspace);
          }
        }),
        catchError((error) => {
          this.logger.error('Error updating workspace', error);
          throw error;
        })
      );
  }

  /**
   * Delete workspace (soft delete).
   */
  deleteWorkspace(id: string): Observable<void> {
    return this.http.delete(`${this.apiAddress}/workspace/${id}`).pipe(
      map(() => {
        // Clear session if deleted workspace was current
        if (this.getCurrentWorkspaceFromStorage() === id) {
          localStorage.removeItem(this.STORAGE_KEY);
          this.currentWorkspace$.next(null);
        }
      }),
      catchError((error) => {
        this.logger.error('Error deleting workspace', error);
        throw error;
      })
    );
  }

  /**
   * Switch current workspace.
   */
  switchWorkspace(id: string): Observable<Workspace> {
    return this.http
      .post<{ code: number; message: string }>(`${this.apiAddress}/workspace/${id}/switch`, {})
      .pipe(
        tap(() => {
          this.setCurrentWorkspace(id);
          // Reload workspace data with membership
          this.getCurrentWorkspace().subscribe();
        }),
        map(() => {
          const workspace = this.currentWorkspace$.value;
          if (!workspace) {
            throw new Error('Workspace not loaded');
          }
          return workspace;
        }),
        catchError((error) => {
          this.logger.error('Error switching workspace', error);
          throw error;
        })
      );
  }

  /**
   * Get workspace usage statistics.
   */
  getWorkspaceUsage(id: string): Observable<WorkspaceUsage> {
    return this.http
      .get<{ code: number; message: string; data: WorkspaceUsage }>(
        `${this.apiAddress}/workspace/${id}/usage`
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error getting workspace usage', error);
          throw error;
        })
      );
  }

  /**
   * Get workspace members and pending invitations.
   */
  getMembers(workspaceId: string): Observable<WorkspaceMembersResponse> {
    return this.http
      .get<{ code: number; message: string; data: WorkspaceMembersResponse }>(
        `${this.apiAddress}/workspace/${workspaceId}/members`
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error getting workspace members', error);
          throw error;
        })
      );
  }

  /**
   * Invite a member to the workspace.
   */
  inviteMember(workspaceId: string, email: string, role: string): Observable<WorkspaceInvitation> {
    return this.http
      .post<{ code: number; message: string; data: WorkspaceInvitation }>(
        `${this.apiAddress}/workspace/${workspaceId}/members/invite`,
        { email, role }
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error inviting member', error);
          throw error;
        })
      );
  }

  /**
   * Update a member's role.
   */
  updateMemberRole(
    workspaceId: string,
    memberId: string,
    role: string
  ): Observable<WorkspaceMember> {
    return this.http
      .put<{ code: number; message: string; data: WorkspaceMember }>(
        `${this.apiAddress}/workspace/${workspaceId}/members/${memberId}`,
        { role }
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error updating member role', error);
          throw error;
        })
      );
  }

  /**
   * Remove a member from the workspace.
   */
  removeMember(workspaceId: string, memberId: string): Observable<void> {
    return this.http.delete(`${this.apiAddress}/workspace/${workspaceId}/members/${memberId}`).pipe(
      map(() => void 0),
      catchError((error) => {
        this.logger.error('Error removing member', error);
        throw error;
      })
    );
  }

  /**
   * Get invitation details by token (public endpoint).
   */
  getInvitationByToken(token: string): Observable<WorkspaceInvitation> {
    return this.http
      .get<{ code: number; message: string; data: WorkspaceInvitation }>(
        `${this.apiAddress}/workspace/invite/${token}`
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Accept a workspace invitation.
   */
  acceptInvitation(token: string): Observable<WorkspaceMember> {
    return this.http
      .post<{ code: number; message: string; data: WorkspaceMember }>(
        `${this.apiAddress}/workspace/invite/accept`,
        { token }
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Cancel a pending invitation.
   */
  cancelInvitation(workspaceId: string, invitationId: string): Observable<void> {
    return this.http
      .delete(`${this.apiAddress}/workspace/${workspaceId}/invitations/${invitationId}`)
      .pipe(
        map(() => void 0),
        catchError((error) => {
          this.logger.error('Error cancelling invitation', error);
          throw error;
        })
      );
  }

  /**
   * Set current workspace ID in localStorage and BehaviorSubject.
   */
  setCurrentWorkspace(workspaceId: string): void {
    localStorage.setItem(this.STORAGE_KEY, workspaceId);
  }

  /**
   * Get current workspace ID from localStorage.
   */
  getCurrentWorkspaceFromStorage(): string | null {
    return localStorage.getItem(this.STORAGE_KEY);
  }

  /**
   * Load workspace from storage and fetch data.
   */
  private loadWorkspaceFromStorage(): void {
    const workspaceId = this.getCurrentWorkspaceFromStorage();
    if (workspaceId) {
      // GLOB-1897: no error handler here — getCurrentWorkspace() never errors.
      // Invalid-workspace cleanup happens in handleCurrentWorkspaceFailure.
      this.getCurrentWorkspace().subscribe();
    }
  }

  /**
   * Get current workspace as Observable.
   */
  getCurrentWorkspaceObservable(): Observable<Workspace | null> {
    return this.currentWorkspace$.asObservable();
  }

  /**
   * Switch workspace and navigate to dashboard.
   */
  switchWorkspaceAndNavigate(id: string): void {
    // Show transition overlay immediately
    this.showWorkspaceTransition();

    this.switchWorkspace(id).subscribe({
      next: () => {
        this.router.navigateByUrl('/dashboard').then(() => {
          window.location.reload();
        });
      },
      error: (error) => {
        this.logger.error('Error switching workspace', error);
        this.hideWorkspaceTransition();
      },
    });
  }

  private showWorkspaceTransition(): void {
    localStorage.setItem('ws_transition', '1');
    const el = document.getElementById('workspace-transition');
    if (el) {
      el.style.display = 'flex';
      el.style.opacity = '1';
    }
  }

  private hideWorkspaceTransition(): void {
    localStorage.removeItem('ws_transition');
    const el = document.getElementById('workspace-transition');
    if (el) {
      el.style.opacity = '0';
      setTimeout(() => {
        el.style.display = 'none';
      }, 400);
    }
  }

  /**
   * Get user's default workspace preference.
   */
  getDefaultWorkspace(): Observable<{ workspace_id: string | null }> {
    return this.http
      .get<{ code: number; message: string; data: { workspace_id: string | null } }>(
        `${this.apiAddress}/user/default-workspace`
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error getting default workspace', error);
          return of({ workspace_id: null });
        })
      );
  }

  /**
   * Set user's default workspace preference.
   */
  setDefaultWorkspace(workspaceId: string): Observable<{ workspace_id: string }> {
    return this.http
      .put<{ code: number; message: string; data: { workspace_id: string } }>(
        `${this.apiAddress}/user/default-workspace`,
        { workspace_id: workspaceId }
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          this.logger.error('Error setting default workspace', error);
          throw error;
        })
      );
  }

  /**
   * Clear user's default workspace preference.
   */
  clearDefaultWorkspace(): Observable<void> {
    return this.http.delete(`${this.apiAddress}/user/default-workspace`).pipe(
      map(() => void 0),
      catchError((error) => {
        this.logger.error('Error clearing default workspace', error);
        throw error;
      })
    );
  }

  /**
   * Leave current workspace.
   */
  leaveWorkspace(workspaceId: string): Observable<void> {
    return this.http.post(`${this.apiAddress}/workspace/${workspaceId}/leave`, {}).pipe(
      map(() => void 0),
      catchError((error) => {
        this.logger.error('Error leaving workspace', error);
        throw error;
      })
    );
  }

  /**
   * Clear current workspace.
   */
  clearCurrentWorkspace(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.currentWorkspace$.next(null);
    this.permissionService.clear();
  }
}
